/**
 * Offline-first sync engine (STANDALONE-APP-PLAN.md §3.2).
 *
 *  Write path:  UI → Dexie row + outbox entry → UI re-renders from Dexie.
 *               The app never waits on the network.
 *  Push:        drain outbox FIFO, batched per (table, op). 401 → pause and
 *               ask for re-auth. RLS/409 → mark that entry `rejected`, surface
 *               it, never retry it silently. Network → offline, try later.
 *  Pull:        per table, rows with updated_at > watermark, soft-deleted rows
 *               included so deletions propagate. Last-write-wins on
 *               updated_at; a row with a pending local edit is left alone
 *               until that edit has been pushed.
 *  Status:      every state change is observable (`subscribe`) so the header
 *               badge can always tell the truth. The v2.25.0 break happened
 *               because failure and success looked identical.
 */
import type { CoachDb } from '../local/db';
import { SYNC_TABLES } from '../local/db';
import { newId, nowIso } from '../ids';
import type { OutboxEntry, OutboxOp, SyncStatus, SyncStatusState, SyncTable } from '../types';
import { RemoteError, type RemoteApi, type RemoteRow } from './remote';

/** Columns the server owns. Never sent in an upsert (ground rule §2.4). */
const SERVER_OWNED = new Set(['updated_at', 'deleted_at']);
const PUSH_BATCH = 50;
/** Byte cap per upsert request, so a run of drills carrying inline photos
 *  (plugin imports) doesn't become one multi-megabyte POST. One oversized row
 *  still goes on its own. */
const PUSH_BATCH_BYTES = 1_000_000;

export interface EngineOptions {
  db: CoachDb;
  remote: RemoteApi;
  /** Called on 401 so the app can prompt for re-auth. */
  onAuthRequired?: () => void;
  log?: (msg: string, extra?: unknown) => void;
}

type Listener = (s: SyncStatus) => void;

export class SyncEngine {
  private db: CoachDb;
  private remote: RemoteApi;
  private listeners = new Set<Listener>();
  private status: SyncStatus = { state: 'synced', pending: 0, rejected: 0, lastError: null, lastSyncAt: null };
  private running: Promise<void> | null = null;
  private online = true;
  private paused = false;
  private onAuthRequired: (() => void) | undefined;
  private log: (msg: string, extra?: unknown) => void;

  constructor(opts: EngineOptions) {
    this.db = opts.db;
    this.remote = opts.remote;
    this.onAuthRequired = opts.onAuthRequired;
    this.log = opts.log ?? (() => {});
  }

  // ─── Observability ───────────────────────────────────────────────────────
  getStatus(): SyncStatus {
    return this.status;
  }
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.status);
    return () => this.listeners.delete(fn);
  }
  private async emit(patch: Partial<SyncStatus> & { state?: SyncStatusState }): Promise<void> {
    const pending = await this.db.outbox.where('rejected').equals(0).count();
    const rejected = await this.db.outbox.where('rejected').equals(1).count();
    let state: SyncStatusState = patch.state ?? this.status.state;
    if (!patch.state) {
      if (this.paused) state = 'paused';
      else if (!this.online) state = 'offline';
      else if (this.status.state === 'error') state = 'error';
      else state = pending > 0 ? 'pending' : 'synced';
    }
    this.status = { ...this.status, ...patch, state, pending, rejected };
    for (const l of this.listeners) l(this.status);
  }

  setOnline(online: boolean): void {
    this.online = online;
    void this.emit({});
  }
  /** After a successful re-auth. */
  resume(): void {
    this.paused = false;
    void this.emit({ lastError: null, state: 'pending' });
  }

  // ─── Write path ──────────────────────────────────────────────────────────
  /**
   * Record a local change and queue it. `row` is the FULL local row (already
   * written to its table by the repo). Consecutive upserts of the same row
   * collapse into one outbox entry so a 900 ms autosave doesn't queue
   * fifty copies of the same plan.
   */
  async enqueue(table: SyncTable, op: OutboxOp, row: { id: string } & Record<string, unknown>): Promise<void> {
    await this.db.transaction('rw', this.db.outbox, async () => {
      const existing = await this.db.outbox.where('[table+row_id]').equals([table, row.id]).sortBy('queued_at');
      const last = existing[existing.length - 1];
      if (last && !last.rejected && last.op === 'upsert' && op === 'upsert') {
        await this.db.outbox.update(last.id, { row: { ...row }, queued_at: Date.now() });
        return;
      }
      const entry: OutboxEntry = {
        id: newId(),
        table,
        op,
        row_id: row.id,
        row: { ...row },
        queued_at: Date.now(),
        attempts: 0,
        last_error: null,
        rejected: 0,
      };
      await this.db.outbox.put(entry);
    });
    await this.emit({});
  }

  /** Ids with a pending (unpushed, not rejected) change. */
  private async pendingIds(table: SyncTable): Promise<Set<string>> {
    const rows = await this.db.outbox.where('table').equals(table).toArray();
    return new Set(rows.filter((r) => !r.rejected).map((r) => r.row_id));
  }

  async rejectedEntries(): Promise<OutboxEntry[]> {
    return this.db.outbox.where('rejected').equals(1).toArray();
  }
  /** The user gave up on a rejected change: drop it and re-pull the server's copy. */
  async discardRejected(entryId: string): Promise<void> {
    await this.db.outbox.delete(entryId);
    await this.emit({});
  }

  // ─── Sync ────────────────────────────────────────────────────────────────
  /** Push then pull. Concurrent calls coalesce into the in-flight run. */
  sync(teamId: string): Promise<void> {
    if (this.running) return this.running;
    this.running = this.run(teamId).finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async run(teamId: string): Promise<void> {
    if (this.paused) return;
    // Every trigger (foreground, interval, network regain) is a fresh
    // attempt: assume online until the network says otherwise.
    this.online = true;
    try {
      await this.push();
      if (this.paused || !this.online) return;
      await this.pull(teamId);
      this.status = { ...this.status, state: 'synced' };
      await this.emit({ lastError: null, lastSyncAt: Date.now() });
    } catch (e) {
      this.handleError(e);
      await this.emit({});
    }
  }

  private handleError(e: unknown): void {
    if (e instanceof RemoteError) {
      if (e.kind === 'auth') {
        this.paused = true;
        this.status = { ...this.status, state: 'paused', lastError: 'Signed out — sign in to keep syncing' };
        this.onAuthRequired?.();
        return;
      }
      if (e.kind === 'network') {
        this.online = false;
        this.status = { ...this.status, state: 'offline', lastError: null };
        return;
      }
      this.status = { ...this.status, state: 'error', lastError: e.message };
      return;
    }
    const msg = e instanceof Error ? e.message : String(e);
    this.status = { ...this.status, state: 'error', lastError: msg };
    this.log('sync error', e);
  }

  // ─── Push ────────────────────────────────────────────────────────────────
  async push(): Promise<void> {
    if (!this.online || this.paused) return;
    for (;;) {
      const all = (await this.db.outbox.orderBy('queued_at').toArray()).filter((e) => !e.rejected);
      if (!all.length) return;
      // Take the head run of entries that share (table, op).
      const head = all[0]!;
      const batch: OutboxEntry[] = [];
      let bytes = 0;
      for (const e of all) {
        if (e.table !== head.table || e.op !== head.op) break;
        const size = e.op === 'upsert' ? JSON.stringify(e.row).length : 0;
        if (batch.length && bytes + size > PUSH_BATCH_BYTES) break;
        batch.push(e);
        bytes += size;
        if (batch.length >= PUSH_BATCH) break;
      }
      await this.pushBatch(batch);
    }
  }

  private async pushBatch(batch: OutboxEntry[]): Promise<void> {
    const table = batch[0]!.table;
    const op = batch[0]!.op;
    try {
      if (op === 'upsert') {
        const payload = batch.map((e) => stripServerOwned(e.row));
        const serverRows = await this.remote.upsert(table, payload);
        await this.adoptServerRows(table, batch, serverRows);
      } else if (op === 'delete') {
        await this.remote.softDelete(table, batch.map((e) => e.row_id));
      } else {
        await this.remote.restore(table, batch.map((e) => e.row_id));
      }
      await this.db.outbox.bulkDelete(batch.map((e) => e.id));
      await this.emit({});
    } catch (e) {
      if (e instanceof RemoteError && e.kind === 'rejected') {
        if (batch.length > 1) {
          // Isolate the bad one: push individually.
          for (const one of batch) await this.pushBatch([one]);
          return;
        }
        const only = batch[0]!;
        await this.db.outbox.update(only.id, {
          rejected: 1,
          attempts: only.attempts + 1,
          last_error: e.message,
        });
        this.log('outbox entry rejected', { table, id: only.row_id, error: e.message });
        await this.emit({});
        return;
      }
      for (const one of batch) {
        await this.db.outbox.update(one.id, {
          attempts: one.attempts + 1,
          last_error: e instanceof Error ? e.message : String(e),
        });
      }
      throw e;
    }
  }

  /**
   * Adopt server timestamps onto the rows we posted — matched BY ID against
   * the snapshot we sent, never by index into a live array (§2.4). If the
   * row was edited again while the request was in flight, leave the local
   * copy alone; the newer outbox entry will carry it up next.
   */
  private async adoptServerRows(table: SyncTable, batch: OutboxEntry[], serverRows: RemoteRow[]): Promise<void> {
    const byId = new Map(serverRows.map((r) => [r.id, r]));
    const t = this.db.table_(table);
    await this.db.transaction('rw', t, this.db.outbox, async () => {
      for (const e of batch) {
        const s = byId.get(e.row_id);
        if (!s) continue;
        const newer = await this.db.outbox
          .where('[table+row_id]')
          .equals([table, e.row_id])
          .filter((x) => x.id !== e.id && !x.rejected)
          .count();
        if (newer > 0) continue;
        const local = await t.get(e.row_id);
        if (!local) continue;
        // Server rows never carry local-only fields; merge so we keep them.
        await t.put({ ...local, ...s, deleted_at: local.deleted_at } as never);
      }
    });
  }

  // ─── Pull ────────────────────────────────────────────────────────────────
  async pull(teamId: string, tables: SyncTable[] = SYNC_TABLES): Promise<void> {
    for (const table of tables) {
      let since = (await this.db.sync_state.get(table))?.last_pulled_at ?? null;
      for (let page = 0; page < 20; page++) {
        const { rows } = await this.remote.pull(table, teamId, since);
        if (!rows.length) break;
        const pending = await this.pendingIds(table);
        const t = this.db.table_(table);
        await this.db.transaction('rw', t, async () => {
          for (const r of rows) {
            if (r.team_id !== teamId) continue;
            if (pending.has(r.id)) continue; // local unsynced edit wins until pushed
            const local = await t.get(r.id);
            if (local && local.updated_at > r.updated_at) continue; // LWW
            await t.put(r as never);
          }
        });
        const max = rows[rows.length - 1]!.updated_at;
        // Watermark = newest row seen, NOT server_now: a row committed
        // between our query and the server's clock read would otherwise be
        // skipped forever (the batch-vs-gap trap).
        since = max;
        await this.db.sync_state.put({ table, last_pulled_at: since });
        if (rows.length < 1000) break;
        // A full page: back off 1 ms so equal timestamps at the boundary aren't lost.
        since = new Date(new Date(max).getTime() - 1).toISOString();
      }
    }
  }
}

export function stripServerOwned(row: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    if (SERVER_OWNED.has(k)) continue;
    if (k.startsWith('_')) continue; // local-only helpers
    out[k] = v;
  }
  return out;
}

export { nowIso };
